import { useEffect, useState, useCallback, useRef } from "react";

// Close codes that mean the server refused this connection on purpose
// (auth / authorization). Reconnecting with the same token cannot fix these,
// so we stop immediately instead of hammering the backend.
const AUTH_REJECT_CODES = new Set([1008, 4401, 4403]);

// Cap retries for transient or ambiguous closes — network drops, or a 403
// rejected during the handshake, which browsers surface as an abnormal 1006
// close rather than 1008. Without a cap a bad token reconnects forever.
const MAX_RECONNECT_ATTEMPTS = 5;
const MAX_RECONNECT_DELAY_MS = 30000;

const useWebSocket = (url) => {
  const [connectionStatus, setConnectionStatus] = useState("");
  const [socketMessage, setSocketMessage] = useState(""); // State to hold the latest incoming message
  const [socket, setSocket] = useState(null);
  const [reconnectTick, setReconnectTick] = useState(0);
  const attemptsRef = useRef(0);

  // Reset the backoff whenever the target URL changes (e.g. a new token), so a
  // fresh connection gets a full set of attempts rather than inheriting the
  // previous URL's exhausted count.
  useEffect(() => {
    attemptsRef.current = 0;
  }, [url]);

  useEffect(() => {
    if (!url || url.includes("undefined") || url.includes("null")) {
      return undefined;
    }

    const ws = new WebSocket(url);
    let reconnectTimer;
    let shouldReconnect = true;

    ws.onopen = () => {
      console.log("WebSocket connection established");
      attemptsRef.current = 0; // A good connection clears any prior backoff.
      setConnectionStatus("connected");
    };

    ws.onmessage = (event) => {
      try {
        setSocketMessage(JSON.parse(event.data)); // Update socketMessage state
      } catch (error) {
        console.error("Invalid WebSocket message:", event.data, error);
      }
    };

    ws.onclose = (event) => {
      setConnectionStatus("disconnected");
      // Cleanup (url change / unmount) sets this false — never reconnect then.
      if (!shouldReconnect) return;

      // Auth rejection: the token is invalid/forbidden. Stop and surface it.
      if (AUTH_REJECT_CODES.has(event.code)) {
        setConnectionStatus("unauthorized");
        return;
      }

      // Transient/ambiguous close: reconnect with capped exponential backoff.
      if (attemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        setConnectionStatus("failed");
        return;
      }
      const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** attemptsRef.current);
      attemptsRef.current += 1;
      reconnectTimer = window.setTimeout(() => {
        setReconnectTick((tick) => tick + 1);
      }, delay);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setConnectionStatus("error");
    };

    setSocket(ws);

    // Cleanup function to close the WebSocket connection
    return () => {
      shouldReconnect = false;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      if (ws) {
        ws.close();
      }
    };
  }, [url, reconnectTick]);

  const sendMessage = useCallback(
    (message) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "message", ...message }));
      } else {
        console.warn("WebSocket is not connected; message was not sent.");
      }
    },
    [socket]
  );

  return { connectionStatus, sendMessage, socketMessage }; // Return socketMessage state
};

export default useWebSocket;
